import axios, { AxiosError } from "axios";
import { env } from "@astro/config";
import { ApiRequestError } from "../errors";

/**
 * LiveKit live-class endpoints — served by the astrology-admin-v2 API
 * (`apps/api` in that repo; dev port 4000, production api.astrorajumaharaj.com),
 * NOT by the Laravel backend behind the auth BFF. That backend has its own
 * auth (Bearer JWT from its POST /api/v1/login) and open CORS, so the browser
 * calls it directly on its own axios instance: no BFF proxy, no session
 * cookie, no 401→refresh interceptor. Base URL: VITE_LIVEKIT_API_URL.
 *
 * Two ways to obtain a join token:
 *
 *   devToken()  — GET /livekit/dev-token. TESTING harness: no auth, no live
 *                 class record; the UI supplies room, identity, name and role.
 *                 The server only answers when it runs with
 *                 LIVEKIT_DEV_TOKENS=true and NODE_ENV !== production (404
 *                 otherwise), so this cannot leak into a real deployment.
 *   token()     — POST /livekit/token { live_class_id } with a Bearer JWT.
 *                 The real, access-controlled path: identity, display name,
 *                 room and host/viewer rights all come from the database.
 *
 * Both hand back `{ token, serverUrl }`, which is all <LiveKitRoom> needs.
 */

/** Publish rights the dev-token endpoint can grant. Host = camera, mic and
 * screen share; publisher (co-host / speaker) = camera + mic; subscriber =
 * watch, listen, chat and raise a hand only. */
export type LivekitDevRole = "host" | "publisher" | "subscriber";

export interface LivekitDevTokenParams {
  /** Room name — letters, numbers, `_` and `-` only (max 64). Created on first join. */
  room: string;
  /** Unique per participant; LiveKit allows one live connection per identity. */
  identity: string;
  /** Roster display name (defaults to identity server-side). */
  name?: string;
  role?: LivekitDevRole;
  /** Token lifetime in seconds, 60–86400 (default 3600). */
  ttl?: number;
}

/** Flat payload (no envelope) — the dev endpoint deliberately doesn't look like the real API. */
export interface LivekitDevTokenResponse {
  token: string;
  serverUrl: string;
  room: string;
  identity: string;
  name: string;
  role: LivekitDevRole;
  canPublish: boolean;
  expiresIn: number;
  warning: string;
}

/** POST /livekit/token payload — the class's stored instructor decides host rights. */
export interface LivekitTokenResponse {
  token: string;
  serverUrl: string;
  roomName: string;
  identity: string;
  displayName: string;
  isHost: boolean;
  canPublish: boolean;
  expiresAt: string;
  liveClass: {
    id: number;
    title: string;
    status: string;
    startTime: string;
    endTime: string;
  };
}

interface Envelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface UpstreamErrorBody {
  message?: string;
  errors?: Record<string, string[]>;
}

/** Thrown before any request when the stream server isn't configured for this build. */
const notConfigured = () =>
  new ApiRequestError({
    code: "livekit_not_configured",
    message: "The live stream server is not configured (VITE_LIVEKIT_API_URL).",
    status: 0,
  });

const livekitHttp = axios.create({
  baseURL: env.VITE_LIVEKIT_API_URL,
  headers: { "Content-Type": "application/json", Accept: "application/json" },
});

// Same normalisation as the shared client so callers can read `.message` /
// `.status` off one error shape everywhere — minus the session-refresh
// retry, which is meaningless against this backend.
livekitHttp.interceptors.response.use(undefined, (error: AxiosError) => {
  if (axios.isCancel(error) || error.code === "ERR_CANCELED") throw error;
  const body = (error.response?.data ?? {}) as UpstreamErrorBody;
  throw new ApiRequestError({
    code: error.response ? "http_error" : "network_error",
    message: body.message ?? error.message,
    status: error.response?.status ?? 0,
    details: body.errors,
  });
});

export const livekit = {
  /** True when this build knows where the stream server lives. */
  isConfigured: () => Boolean(env.VITE_LIVEKIT_API_URL),

  /** Testing-only join token — see module doc. */
  devToken: async (params: LivekitDevTokenParams) => {
    if (!env.VITE_LIVEKIT_API_URL) throw notConfigured();
    const res = await livekitHttp.get<LivekitDevTokenResponse>("/livekit/dev-token", { params });
    return res.data;
  },

  /** Real join token for an enrolled user / the class instructor. `bearer`
   * is the astrology-admin-v2 JWT (its POST /api/v1/login), not the BFF session. */
  token: async (liveClassId: number, bearer: string) => {
    if (!env.VITE_LIVEKIT_API_URL) throw notConfigured();
    const res = await livekitHttp.post<Envelope<LivekitTokenResponse>>(
      "/livekit/token",
      { live_class_id: liveClassId },
      { headers: { Authorization: `Bearer ${bearer}` } }
    );
    return res.data.data;
  },
};
